import { useState } from "react";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { api, formatApiError } from "@/lib/api";
import { useFormSecurity } from "@/lib/useFormSecurity";
import { usePrivacyAcceptance } from "@/lib/usePrivacyAcceptance";
import { CheckCircle2, Mail, MapPin } from "lucide-react";

const CONTACT_IMG =
  "https://images.unsplash.com/photo-1747727568150-444573cd705a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZ3xlbnwwfHx8fDE3Nzc5NTg3NzR8MA&ixlib=rb-4.1.0&q=85";

export default function ContactPage() {
  const content = useSiteContent();
  const copy = content?.contact || {};
  const { getSignals } = useFormSecurity();
  const { accepted, version, AcknowledgementCheckbox, validate } = usePrivacyAcceptance();

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) {
      setError("Please read and accept the Privacy Policy before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contacts", {
        ...form,
        ...getSignals(),
        privacy_accepted: accepted,
        privacy_policy_version: version,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicShell>
      <section className="border-b border-border" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
            <div className="overline mb-4">Contact</div>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02] text-obsidian">
              {copy.title || "Contact"}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              {copy.subtitle}
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 border border-border">
              <div className="p-6 border-b sm:border-b-0 sm:border-r border-border">
                <Mail className="h-5 w-5 text-execution mb-3" />
                <div className="overline mb-2">Email</div>
                <a
                  href={`mailto:${copy.email || "hello@nexra.com"}`}
                  className="font-heading text-lg hover:text-execution transition-colors"
                  data-testid="contact-email-link"
                >
                  {copy.email || "hello@nexra.com"}
                </a>
              </div>
              <div className="p-6">
                <MapPin className="h-5 w-5 text-execution mb-3" />
                <div className="overline mb-2">Operating model</div>
                <p className="font-heading text-lg leading-snug">
                  {copy.address || "Delivery Leadership. Where it is needed."}
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 relative min-h-[240px] lg:min-h-full border-l border-border overflow-hidden">
            <img src={CONTACT_IMG} alt="Architectural contrast" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="section max-w-[1100px]" data-testid="contact-form-section">
        {done ? (
          <div className="border border-border p-10 lg:p-14 bg-muted" data-testid="contact-confirmation">
            <CheckCircle2 className="h-8 w-8 text-execution mb-6" />
            <h2
              className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight"
              data-testid="contact-success-title"
            >
              {copy.success_title || "Message received. Nexra will be in contact shortly."}
            </h2>
            <p
              className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl"
              data-testid="contact-success-body"
            >
              {copy.success_body || "Thank you for reaching out. A member of the Nexra team will review your message and respond as soon as possible."}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="border border-border p-5 sm:p-6 lg:p-10 bg-white" data-testid="contact-form">
            <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight mb-6 sm:mb-8">Send a message</h2>
            {/* Honeypot */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Website</label>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" required>
                <input className="input-swiss" required value={form.name} onChange={update("name")} autoComplete="name" data-testid="contact-input-name" />
              </Field>
              <Field label="Email" required>
                <input className="input-swiss" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" autoCorrect="off" required value={form.email} onChange={update("email")} data-testid="contact-input-email" />
              </Field>
              <Field label="Phone">
                <input className="input-swiss" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" />
              </Field>
            </div>
            <Field className="mt-5" label="Message" required>
              <textarea
                className="input-swiss min-h-[140px] resize-y"
                required
                value={form.message}
                onChange={update("message")}
                data-testid="contact-input-message"
              />
            </Field>
            {error && <div className="mt-4 text-sm text-destructive" data-testid="contact-error">{error}</div>}
            <div className="mt-6">
              <AcknowledgementCheckbox testid="contact-privacy-accept" />
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-3">
              <button type="submit" disabled={submitting || !accepted} className="btn-primary disabled:opacity-60 w-full sm:w-auto" data-testid="contact-submit">
                {submitting ? "Sending..." : "Send message"}
              </button>
            </div>
          </form>
        )}
      </section>
    </PublicShell>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
        {label}
        {required && <span className="text-execution"> *</span>}
      </span>
      {children}
    </label>
  );
}
